
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { LanguageToggle } from './LanguageToggle';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';
import { Menu, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const Header = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const articles = [
    { id: "geschichte-der-hundeschule", title: "Geschichte der Hundeschule" },
    { id: "wieso-eine-hundeschule", title: "Wieso eine Hundeschule?" },
    { id: "woran-erkennt-man-eine-gute-hundeschule", title: "Woran erkennt man eine gute Hundeschule?" },
    { id: "alles-wichtige-zum-thema-hundesteuer", title: "Alles Wichtige zum Thema Hundesteuer" },
    { id: "welcher-hund-passt-zu-mir", title: "Welcher Hund passt zu mir?" },
    { id: "grundausstattung-fuer-ihren-hund", title: "Grundausstattung für Ihren Hund" },
    { id: "indoor-aktivitaeten-fuer-ihren-hund", title: "Indoor-Aktivitäten für Ihren Hund" },
    { id: "warum-hundesport-so-wichtig-ist", title: "Warum Hundesport so wichtig ist" },
    { id: "hunde-allein-zuhause", title: "Hunde allein zuhause" },
    { id: "nahrungsergaenzungsmittel-fuer-hunde", title: "Nahrungsergänzungsmittel für Hunde" },
    { id: "barf-als-hundefutter", title: "BARF als Hundefutter" },
    { id: "naturbelassenes-hundefutter", title: "Naturbelassenes Hundefutter" },
    { id: "markknochen-als-hundesnack", title: "Markknochen als Hundesnack" },
    { id: "beste-ernaehrung-fuer-den-hund", title: "Beste Ernährung für den Hund" },
    { id: "spartipps-fuer-tierfutter", title: "Spartipps für Tierfutter" },
    { id: "zahngesundheit-fuer-hunde", title: "Zahngesundheit für Hunde" },
    { id: "verhaltensstoerungen-beim-hund", title: "Verhaltensstörungen beim Hund" },
    { id: "orthopaedisches-hundebett", title: "Orthopädisches Hundebett" },
    { id: "hund-entlaufen", title: "Hund entlaufen" },
    { id: "hunde-und-welpen", title: "Hunde und Welpen" },
    { id: "gassi-gehen-mit-welpen", title: "Gassi gehen mit Welpen" },
    { id: "hundekrankenversicherung", title: "Hundekrankenversicherung" },
    { id: "zeckenschutz-fuer-hunde", title: "Zeckenschutz für Hunde" },
  ];

  // Define popular dog breeds for dropdown
  const dogBreeds = [
    { id: "labrador-retriever", title: "Labrador Retriever" },
    { id: "deutscher-schaeferhund", title: "Deutscher Schäferhund" },
    { id: "golden-retriever", title: "Golden Retriever" },
    { id: "franzoesische-bulldogge", title: "Französische Bulldogge" },
    { id: "bulldogge", title: "Bulldogge" },
    { id: "beagle", title: "Beagle" },
    { id: "pudel", title: "Pudel" },
    { id: "rottweiler", title: "Rottweiler" },
    { id: "yorkshire-terrier", title: "Yorkshire Terrier" },
  ];

  return (
    <motion.header 
      initial={{ y: -50, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 100, duration: 0.5 }}
      className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200 py-4 px-6 md:px-10 flex flex-col md:flex-row justify-between items-center shadow-sm"
    >
      <div className="flex w-full md:w-auto justify-between items-center">
        <motion.div
          whileHover={{ scale: 1.05 }}
          transition={{ type: 'spring', stiffness: 400, damping: 10 }}
          className="flex items-center gap-3 cursor-pointer"
          onClick={() => navigate('/')}
        >
          <h1 className="text-xl md:text-2xl font-semibold text-primary">{t('siteTitle')}</h1>
          <img 
            src="/lovable-uploads/5dcadb59-e79b-45de-851e-4a6add76a6cc.png" 
            alt="Hundeschulenfinder Logo - Finde die beste Hundeschule in deiner Nähe" 
            className="h-8 md:h-10"
          />
        </motion.div>
        
        <Button 
          variant="ghost" 
          className="md:hidden p-1" 
          onClick={toggleMobileMenu}
        >
          <Menu size={24} />
        </Button>
      </div>
      
      <div className={`${mobileMenuOpen ? 'flex' : 'hidden'} md:flex flex-col md:flex-row items-center gap-4 w-full md:w-auto mt-4 md:mt-0`}>
        {/* Ratgeber direct link and dropdown menu */}
        <div className="relative md:flex items-center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                className="flex items-center gap-1 text-sm md:text-base text-gray-700 hover:text-primary transition-colors"
                onClick={() => navigate('/ratgeber')}
              >
                Ratgeber
                <ChevronDown size={16} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64 max-h-96 overflow-y-auto">
              {articles.map((article) => (
                <DropdownMenuItem key={article.id}>
                  <Link 
                    to={`/ratgeber#${article.id}`} 
                    className="w-full text-sm text-gray-700 hover:text-primary"
                  >
                    {article.title}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Hunderassen direct link and dropdown menu */}
        <div className="relative md:flex items-center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                className="flex items-center gap-1 text-sm md:text-base text-gray-700 hover:text-primary transition-colors"
                onClick={() => navigate('/hunderassen')}
              >
                Hunderassen
                <ChevronDown size={16} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64 max-h-96 overflow-y-auto">
              <DropdownMenuItem>
                <Link 
                  to="/hunderassen" 
                  className="w-full text-sm font-medium text-gray-700 hover:text-primary"
                >
                  Alle Hunderassen anzeigen
                </Link>
              </DropdownMenuItem>
              
              {dogBreeds.map((breed) => (
                <DropdownMenuItem key={breed.id}>
                  <Link 
                    to={`/hunderassen#${breed.id}`} 
                    className="w-full text-sm text-gray-700 hover:text-primary"
                  >
                    {breed.title}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <Link to="/for-dog-schools" className="text-sm md:text-base text-gray-700 hover:text-primary transition-colors py-2 md:py-0">
          Du hast eine Hundeschule?
        </Link>
        <Link to="/find-right-school" className="text-sm md:text-base text-gray-700 hover:text-primary transition-colors py-2 md:py-0">
          Die richtige Hundeschule
        </Link>
        <LanguageToggle />
      </div>
    </motion.header>
  );
};
